import { useState } from 'react';
import { Alert, Box, Button, FormField, Input, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { PRIORITY_BG, PRIORITY_BORDER, PRIORITY_LEGEND_BG } from '../../../utils/priority';
import type { PriorityTier } from '../../../utils/priority';

interface AddInstanceModalProps {
  topicName: string;
  saving: boolean;
  onClose: () => void;
  /** Resolves `null` on success (caller closes on null), or a ready-to-show failure
   *  message (already correct about retry-ability — a 403 must not say 请重试). */
  onSave: (input: {
    subTopicName: string;
    industry: string;
    notes: 'S' | 'R' | 'O';
  }) => Promise<string | null>;
}

const PRIORITY_OPTIONS: { value: 'S' | 'R' | 'O'; tier: PriorityTier; label: string }[] = [
  { value: 'S', tier: 'strategic', label: 'Strategic' },
  { value: 'R', tier: 'recommended', label: 'Recommended' },
  { value: 'O', tier: 'opportunistic', label: 'Opportunistic' },
];

export default function AddInstanceModal({ topicName, saving, onClose, onSave }: AddInstanceModalProps) {
  const [subTopicName, setSubTopicName] = useState('');
  const [industry, setIndustry] = useState('');
  const [priority, setPriority] = useState<'S' | 'R' | 'O'>('S');
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    const trimmed = subTopicName.trim();
    if (!trimmed) {
      setError('UC 名称不能为空');
      return;
    }
    const failure = await onSave({
      subTopicName: trimmed,
      industry: industry.trim() || '_',
      notes: priority,
    });
    if (failure) setError(failure);
  };

  return (
    <Modal
      visible
      onDismiss={() => {
        if (!saving) onClose();
      }}
      header={`添加UC Topic "${topicName}"`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={!subTopicName.trim()}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="small">为该 Topic 关联一个具体 UC 实例</Box>

        <FormField label="Name *">
          <Input
            value={subTopicName}
            onChange={({ detail }) => setSubTopicName(detail.value)}
            disabled={saving}
            placeholder="e.g. 客户尽调"
            autoFocus
          />
        </FormField>

        <FormField label="Industry（可选）">
          <Input
            value={industry}
            onChange={({ detail }) => setIndustry(detail.value)}
            disabled={saving}
            placeholder="留空 = 跨行业"
          />
        </FormField>

        <FormField label="Type *">
          <div className="ivt-priority-radio-group" role="radiogroup" aria-label="优先级">
            {PRIORITY_OPTIONS.map((opt) => {
              const selected = priority === opt.value;
              return (
                <label
                  key={opt.value}
                  className={
                    'ivt-priority-radio' + (selected ? ' ivt-priority-radio--selected' : '')
                  }
                  style={{
                    background: PRIORITY_BG[opt.tier],
                    borderColor: selected ? PRIORITY_LEGEND_BG[opt.tier] : PRIORITY_BORDER[opt.tier],
                  }}
                >
                  <input
                    type="radio"
                    name="priority"
                    value={opt.value}
                    checked={selected}
                    disabled={saving}
                    onChange={() => setPriority(opt.value)}
                  />
                  <span
                    className="ivt-priority-radio__chip"
                    style={{ background: PRIORITY_LEGEND_BG[opt.tier] }}
                  >
                    {opt.value}
                  </span>
                  <span className="ivt-priority-radio__label">{opt.label}</span>
                </label>
              );
            })}
          </div>
        </FormField>

        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
